export default {
  id: 7,
  slug: '2026-03-pet-emergency-preparedness',
  title: '寵物緊急應變準備：在危機中保護您的寵物的全面指南',
  excerpt: '關於建立緊急計劃、準備急救包、識別危急症狀，以及在自然災害和醫療緊急情況下確保寵物安全的專業指導。',
  content: `
    <p>緊急情況本質上是不可預測的。無論是自然災害、突發疾病還是意外傷害，做好準備可能意味著您寵物的生與死之差。</p>

    <p>這份全面指南將幫助您建立強大的緊急計劃，為您提供必要的知識，並確保您準備好在分秒必爭時保護您心愛的夥伴。</p>

    <h2>為什麼緊急準備很重要</h2>

    <p>考慮這些令人清醒的統計數據：</p>

    <ul>
      <li>卡崔娜颶風期間，估計有 600,000 隻寵物被殺、受傷或流離失所</li>
      <li>63% 的寵物主人沒有為寵物制定緊急疏散計劃</li>
      <li>有緊急計劃的寵物主人安全疏散的可能性是其他人的 3 倍</li>
      <li>醫療緊急情況的前 3 分鐘通常是最關鍵的</li>
      <li>了解基本急救可以穩定寵物直到獲得專業幫助</li>
    </ul>

    <h2>醫療緊急情況識別：知道何時採取行動</h2>

    <h3>危及生命的緊急情況（立即尋求獸醫照護）</h3>

    <p><strong>呼吸問題：</strong></p>
    <ul>
      <li>呼吸困難、喘氣</li>
      <li>牙齦/舌頭呈藍色或蒼白</li>
      <li>窒息、嘔吐但沒有產生任何東西</li>
      <li>失去知覺或虛脫</li>
      <li>嚴重咳嗽伴有血液</li>
    </ul>

    <p><strong>心血管問題：</strong></p>
    <ul>
      <li>脈搏微弱或快速（正常：狗 60-140 bpm，貓 140-220 bpm）</li>
      <li>儘管環境溫暖，四肢仍然冰冷</li>
      <li>突然虛脫或虛弱</li>
      <li>腹部膨脹、堅硬（特別是大型犬——可能是胃扭轉/GDV）</li>
    </ul>

    <p><strong>神經系統症狀：</strong></p>
    <ul>
      <li>癲癇發作持續超過 2 分鐘或多次發作</li>
      <li>失去知覺</li>
      <li>突然失明</li>
      <li>頭部傾斜、繞圈、迷失方向</li>
      <li>無法站立或行走</li>
      <li>任何肢體癱瘓</li>
    </ul>

    <p><strong>嚴重創傷：</strong></p>
    <ul>
      <li>被汽車或其他車輛撞擊</li>
      <li>從高處墜落</li>
      <li>深度傷口伴有大量出血</li>
      <li>可見骨折</li>
      <li>穿透性傷害（咬傷、刺穿）</li>
    </ul>

    <p><strong>中毒/有毒物質攝入：</strong></p>
    <ul>
      <li>目擊攝入有毒物質</li>
      <li>突然嘔吐、腹瀉、流口水</li>
      <li>震顫、癲癇發作</li>
      <li>極度昏睡或過度活躍</li>
      <li>任何孔口出血而無傷害</li>
    </ul>

    <p><strong>常見寵物毒物：</strong></p>
    <ul>
      <li><strong>食物：</strong>巧克力、葡萄/葡萄乾、木糖醇（無糖產品）、洋蔥、大蒜、夏威夷果、酒精</li>
      <li><strong>藥物：</strong>布洛芬、對乙酰氨基酚、抗抑鬱藥、ADHD 藥物</li>
      <li><strong>植物：</strong>百合（貓）、蘇鐵、杜鵑花、鬱金香、水仙花</li>
      <li><strong>家用品：</strong>防凍劑、滅鼠劑、清潔產品、精油</li>
    </ul>

    <blockquote>
      <strong>緊急聯絡資訊</strong>
      <p>將這些重要聯絡人儲存在您的手機和 Pet Care Note 中以便快速訪問：</p>
      <ul>
        <li>您的主要獸醫師</li>
        <li>最近的 24 小時緊急獸醫診所</li>
        <li>當地動物毒物控制中心</li>
        <li>備用緊急聯絡人（如果您無法照顧寵物時可以照顧您的寵物）</li>
      </ul>
      <p><strong>注意：</strong>緊急熱線號碼因國家和地區而異。搜尋「動物毒物控制」或「緊急獸醫服務」以找到當地資源。許多國家都有由動物福利組織或獸醫協會運營的專門獸醫緊急熱線。</p>
    </blockquote>

    <h2>寵物急救包必需品</h2>

    <p>每位寵物主人都應該有專用的急救包。以下是應包括的內容：</p>

    <h3>醫療用品</h3>
    <ul>
      <li><strong>紗布墊和紗布卷</strong>（各種尺寸用於傷口覆蓋和包紮）</li>
      <li><strong>不黏繃帶</strong>（不會黏附傷口）</li>
      <li><strong>醫用膠帶</strong>（用於固定繃帶）</li>
      <li><strong>棉球和棉籤</strong></li>
      <li><strong>3% 過氧化氫</strong>（僅在獸醫或毒物控制中心指示時用於催吐）</li>
      <li><strong>生理鹽水</strong>（用於沖洗傷口、眼睛沖洗）</li>
      <li><strong>抗生素軟膏</strong>（非藥用、寵物安全）</li>
      <li><strong>止血粉</strong>（停止修剪指甲時的出血）</li>
      <li><strong>即時冰袋</strong></li>
      <li><strong>溫度計</strong>（數位直腸溫度計）</li>
      <li><strong>凡士林</strong>（溫度計潤滑劑）</li>
      <li><strong>鑷子</strong>（用於移除碎片、蜱蟲）</li>
      <li><strong>剪刀</strong>（鈍頭用於剪繃帶、膠帶）</li>
      <li><strong>指甲剪</strong></li>
      <li><strong>滴管或注射器</strong>（無針頭，用於口服藥物/液體）</li>
    </ul>

    <h3>約束和安全</h3>
    <ul>
      <li><strong>口罩</strong>（即使是友好的寵物在受傷/害怕時也可能咬人）</li>
      <li><strong>牽繩和項圈</strong></li>
      <li><strong>毛巾</strong>（用於約束、保暖、清潔）</li>
      <li><strong>毯子</strong>（用於保暖、防止休克、運輸）</li>
      <li><strong>手電筒和備用電池</strong></li>
    </ul>

    <h3>文件</h3>
    <ul>
      <li><strong>寵物病歷</strong>（疫苗接種歷史、當前藥物、慢性病）</li>
      <li><strong>近期照片</strong>（用於識別，如果分離）</li>
      <li><strong>緊急聯絡號碼</strong></li>
      <li><strong>寵物急救手冊或快速參考指南</strong></li>
    </ul>

    <blockquote>
      <strong>數位健康記錄</strong>
      <p>在緊急情況下，您可能沒有時間收集文件。使用 Pet Care Note 以數位方式儲存疫苗接種記錄、藥物清單、過敏資訊和緊急聯絡人。您可以從任何裝置、任何地方訪問此資訊——即使您離家。</p>
    </blockquote>

    <h2>基本急救程序</h2>

    <h3>如何測量生命體徵</h3>

    <p><strong>體溫：</strong></p>
    <ol>
      <li>使用數位直腸溫度計</li>
      <li>用凡士林潤滑</li>
      <li>插入 1 英寸（小型寵物）至 2-3 英寸（大型犬）</li>
      <li>等待嗶聲（通常 60 秒）</li>
      <li><strong>正常範圍：</strong>狗：101-102.5°F (38.3-39.2°C)，貓：100.5-102.5°F (38-39.2°C)</li>
    </ol>

    <p><strong>心率/脈搏：</strong></p>
    <ol>
      <li>將手放在肘部後面的左側胸部</li>
      <li>計數 15 秒的心跳，乘以 4</li>
      <li><strong>正常範圍：</strong>狗：60-140 bpm（大型犬較慢，幼犬較快），貓：140-220 bpm</li>
    </ol>

    <p><strong>呼吸頻率：</strong></p>
    <ol>
      <li>觀察胸部起伏</li>
      <li>計數 15 秒的呼吸，乘以 4</li>
      <li><strong>正常範圍：</strong>狗：10-30 次/分鐘，貓：20-30 次/分鐘</li>
    </ol>

    <h3>出血控制</h3>
    <ol>
      <li><strong>施加直接壓力：</strong>使用乾淨的紗布或布，用力按壓 3-5 分鐘</li>
      <li><strong>不要移除第一層：</strong>如果血液浸透，在上面添加更多紗布</li>
      <li><strong>如果可能抬高：</strong>如果傷害在肢體上，抬高到心臟水平以上</li>
      <li><strong>絕不使用止血帶</strong>除非危及生命的出血無法以其他方式停止（可能導致組織死亡）</li>
      <li><strong>立即尋求獸醫照護</strong>任何重大出血</li>
    </ol>

    <h3>窒息</h3>
    <ol>
      <li><strong>檢查口腔：</strong>張開嘴，尋找可見的阻塞物</li>
      <li><strong>手指掃除：</strong>如果您能看到物體並安全抓住它，將其移除（小心——驚慌的寵物可能會咬人）</li>
      <li><strong>哈姆立克法（狗）：</strong>
        <ul>
          <li>小型犬：背部靠著您的胸部抱住，在肋骨下方握拳，向內向上推 5 次</li>
          <li>大型犬：站在後面，雙臂環繞腹部，在肋骨下方握拳，向內向上推 5 次</li>
        </ul>
      </li>
      <li><strong>貓：</strong>背部靠著您的胸部抱住，在肋骨下方握拳，向內向上推</li>
      <li><strong>每組推壓後再次檢查口腔</strong></li>
      <li><strong>如果失去知覺：</strong>開始 CPR 並立即就醫</li>
    </ol>

    <h3>CPR（心肺復甦術）</h3>

    <p><strong>何時執行 CPR：</strong>僅當寵物失去知覺且不呼吸，並且沒有心跳時</p>

    <p><strong>步驟：</strong></p>
    <ol>
      <li><strong>氣道：</strong>將寵物放在右側，伸展頭部和頸部，檢查口腔/喉嚨是否有阻塞</li>
      <li><strong>呼吸：</strong>閉上寵物的嘴，將您的嘴放在它們的鼻子上，給予 2 次呼吸（胸部應該上升）</li>
      <li><strong>按壓：</strong>
        <ul>
          <li>狗/貓：將手放在胸部最寬的部分</li>
          <li>按壓深度：胸寬的 1/3 至 1/2</li>
          <li>速率：每分鐘 100-120 次按壓</li>
          <li>比例：30 次按壓對 2 次呼吸</li>
        </ul>
      </li>
      <li><strong>繼續：</strong>執行 30:2 的循環，直到寵物自己呼吸或您到達獸醫照護</li>
    </ol>

    <h2>自然災害準備</h2>

    <h3>疏散計劃</h3>

    <p><strong>災害發生前：</strong></p>
    <ul>
      <li><strong>識別寵物友好的避難所/酒店</strong>在您的疏散路線中（許多緊急避難所不接受寵物）</li>
      <li><strong>找到寄宿設施</strong>在您的直接區域之外</li>
      <li><strong>安排備用照顧者</strong>（如果您分離，可以帶走您的寵物的朋友/家人）</li>
      <li><strong>確保微晶片資訊是最新的</strong></li>
      <li><strong>始終讓寵物佩戴帶有 ID 標籤的項圈</strong></li>
      <li><strong>練習與寵物一起疏散</strong>（籠子訓練使這更容易）</li>
      <li><strong>了解您的疏散路線</strong>並有備用計劃</li>
    </ul>

    <p><strong>疏散包（準備好隨時拿取）：</strong></p>
    <ul>
      <li><strong>寵物籠/箱</strong>（每隻寵物一個，標有您的聯絡資訊）</li>
      <li><strong>2 週的食物和水供應</strong></li>
      <li><strong>手動開罐器</strong></li>
      <li><strong>食物/水碗</strong></li>
      <li><strong>藥物</strong>（最少 2 週供應）</li>
      <li><strong>病歷</strong>（疫苗接種記錄、處方、病史）</li>
      <li><strong>急救包</strong></li>
      <li><strong>牽繩、項圈、背帶</strong></li>
      <li><strong>貓砂盆和貓砂</strong>（貓）</li>
      <li><strong>便便袋</strong>（狗）</li>
      <li><strong>舒適物品</strong>（最喜歡的玩具、帶有熟悉氣味的毯子）</li>
    </ul>

    <h2>災害特定考量</h2>

    <h3>颶風/洪水</h3>
    <ul>
      <li>提早疏散（不要等待強制疏散）</li>
      <li>絕不留下寵物（如果對您不安全，對它們也不安全）</li>
      <li>文件/藥物的防水容器</li>
      <li>如需要水上疏散的漂浮裝置</li>
    </ul>

    <h3>野火</h3>
    <ul>
      <li>在第一次警告時疏散（火災迅速蔓延）</li>
      <li>計劃多條疏散路線</li>
      <li>疏散期間用濕毛巾保護免受煙霧</li>
      <li>如果必須留下寵物，警告當局（標記房屋）</li>
    </ul>

    <h3>地震</h3>
    <ul>
      <li>期間：躲在堅固的家具下，保護頭部</li>
      <li>將寵物限制（餘震可能導致逃脫嘗試）</li>
      <li>震動停止後檢查是否受傷</li>
      <li>在允許寵物漫遊之前檢查房屋是否有結構損壞</li>
      <li>準備好應對餘震</li>
    </ul>

    <h2>建立寵物緊急計劃</h2>

    <h3>逐步規劃</h3>

    <ol>
      <li><strong>評估您的風險：</strong>您所在地區常見哪些災害？（洪水、地震、颶風、野火）</li>
      <li><strong>建立疏散計劃：</strong>
        <ul>
          <li>主要和次要疏散路線</li>
          <li>沿路線的寵物友好酒店/避難所清單</li>
          <li>直接區域外的寄宿設施聯絡方式</li>
          <li>如果家人分離的會面地點</li>
        </ul>
      </li>
      <li><strong>組裝緊急包：</strong>
        <ul>
          <li>一個在家（2 週的完整供應）</li>
          <li>一個在車上（3 天供應，始終在車輛中）</li>
          <li>快速疏散的隨身包</li>
        </ul>
      </li>
      <li><strong>更新識別：</strong>
        <ul>
          <li>使用當前聯絡資訊註冊的微晶片</li>
          <li>帶有電話號碼的 ID 標籤</li>
          <li>近期照片</li>
          <li>獨特特徵的書面描述</li>
        </ul>
      </li>
      <li><strong>指定備用照顧者：</strong>
        <ul>
          <li>當地朋友/家人（如果您受傷/無法照顧）</li>
          <li>區域外聯絡人（如果當地區域受影響）</li>
          <li>向他們提供鑰匙、寵物照護說明、獸醫資訊</li>
        </ul>
      </li>
      <li><strong>練習您的計劃：</strong>
        <ul>
          <li>練習將寵物放入籠子</li>
          <li>模擬疏散（實際裝車、駕駛路線）</li>
          <li>與所有家庭成員一起審查計劃</li>
        </ul>
      </li>
    </ol>

    <h2>特殊考量</h2>

    <h3>老年或有特殊需求的寵物</h3>
    <ul>
      <li>額外藥物（緊急情況期間可能難以補充）</li>
      <li>醫療設備（胰島素注射器、霧化器等）</li>
      <li>行動輔助（坡道、吊帶、輪椅）</li>
      <li>舒適物品（骨科床、加熱墊）</li>
      <li>詳細的病史和照護說明</li>
    </ul>

    <h3>多隻寵物</h3>
    <ul>
      <li>每隻寵物的個別籠子（疏散期間不要將它們關在一起——壓力可能導致攻擊性）</li>
      <li>每隻寵物的顏色編碼用品</li>
      <li>單獨的食物/藥物清楚標記</li>
      <li>考慮尋求疏散幫助</li>
    </ul>

    <h2>最後的想法</h2>

    <p>希望最好的，但為最壞的做準備。緊急準備不是生活在恐懼中——而是足夠愛您的寵物以確保無論發生什麼都能保證它們的安全。</p>

    <p>您現在投入的時間來建立緊急計劃、組裝用品和學習關鍵技能可能會挽救您寵物的生命。當災難來襲時，您將沒有時間準備。您只有時間行動。</p>

    <p>今天開始。建立您的計劃，建立您的包，學習基本急救。您的寵物不僅依賴您進行日常照護，還依賴您在最重要的時候提供保護。準備好在它們需要您時成為它們的英雄。</p>

    <p>記住：<strong>如果對您不安全，對您的寵物也不安全。</strong>絕不留下它們。它們是家人，家人會在一起——無論發生什麼。</p>
  `,
  author: 'Pet Care Note 團隊',
  publishDate: '2026-03-18',
  readTime: 20,
  category: 'health',
  tags: ['緊急情況', '急救', '災害準備', '安全', '寵物照護'],
  image: 'https://images.unsplash.com/photo-1564760055775-d63b17a55c44?w=1200&q=80'
};